import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Card } from '../card/card';
import { ZagIcon } from '../icon/icon';
import { ColorContrastService } from '../services/color-contrast.service';
import { LocationListRowData } from '../location-list-row/location-list-row';

/**
 * Re-export LocationListRowData as LocationCardData for consistency
 */
export type LocationCardData = LocationListRowData;

/**
 * LocationCard Component
 *
 * Displays a location's basic information in a card format.
 * Uses the Card component for consistent styling.
 *
 * @example
 * ```html
 * <zag-location-card
 *   [location]="locationData"
 *   [editRoute]="['/locations', locationData.id]"
 *   (cardClick)="onLocationSelected($event)"
 *   (deleteClick)="onDeleteLocation($event)">
 * </zag-location-card>
 * ```
 */
@Component({
  selector: 'zag-location-card',
  templateUrl: './location-card.html',
  styleUrls: ['./location-card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Card, ZagIcon],
  host: {
    '[class.zag-location-card]': 'true',
  },
})
export class LocationCard {
  private readonly router = inject(Router);
  private readonly colorContrast = inject(ColorContrastService);

  /** The location data to display */
  readonly location = input.required<LocationCardData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Whether to show the delete button */
  readonly showDelete = input(false);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Emitted when the card is clicked */
  readonly cardClick = output<LocationCardData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<LocationCardData>();

  /** Handle card click - navigates to editRoute if set, then emits cardClick */
  onCardClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.cardClick.emit(this.location());
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.location());
  }

  protected getIconColor(backgroundColor: string | null | undefined): string {
    return this.colorContrast.getIconColor(backgroundColor);
  }
}
